<template>
  <nav class="relative hidden w-full min-[1024px]:block">
    <div
      ref="buttonGroupRef"
      class="relative z-50 flex justify-between bg-avu-gray px-[37px] pb-[22px] pt-[70px] xl:px-[67px]"
    >
      <MainNavButton
        v-for="(button, index) in headerButtons"
        :key="button.key"
        :class="[
          'uppercase',
          {
            'text-avu-blue':
              button.key.startsWith(currentPath) && currentPath.length > 0,
            'text-avu-blue underline underline-offset-8':
              button.key === currentOpenMenu,
          },
        ]"
        :text="button.text"
        :link="button.link"
        :has-links="button.links && button.links.length > 0"
        :disabled="isMenuOpen && button.links?.length > 0"
        @click="
          () =>
            !(isMenuOpen && button.links?.length > 0) &&
            handleClick(index, button)
        "
        @mouseenter="() => handleMouseEnter(button)"
      />

      <div
        v-if="isMenuOpen"
        class="absolute bottom-0 left-[37px] right-[37px] h-[1px] bg-avu-black xl:left-[67px] xl:right-[67px]"
      />
    </div>

    <Transition
      enter-active-class="duration-500 ease-out"
      enter-from-class="-translate-y-full"
      enter-to-class="translate-y-0"
      leave-active-class="duration-500 ease-in"
      leave-from-class="translate-y-0"
      leave-to-class="-translate-y-full"
    >
      <div
        v-if="isMenuOpen"
        class="absolute left-0 top-full z-50 flex h-[calc(100dvh-120px)] w-full items-end bg-avu-gray px-[37px] pb-[22px] transition-transform xl:px-[67px]"
      >
        <div class="flex h-full w-full flex-col justify-between">
          <div class="mt-6 flex flex-col">
            <Transition
              mode="out-in"
              enter-active-class="duration-300 ease-out"
              enter-from-class="opacity-0"
              enter-to-class="opacity-100"
              leave-active-class="duration-300 ease-in"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <div
                v-if="subLinks"
                ref="buttonGroupRef"
                :key="currentOpenMenu"
                class="mb-6 flex min-w-full items-center gap-[27px] xl:gap-[57px]"
              >
                <NavButton
                  v-for="(link, index) in subLinks"
                  :key="link.text + '-' + index"
                  :text="link.text"
                  :link="link.link"
                  :class="{ 'text-avu-blue': index === hoveredSubLinkIndex }"
                  @mouseenter="handleSubLinkHover(index)"
                />
              </div>
            </Transition>

            <Transition
              mode="out-in"
              enter-active-class="duration-300 ease-out"
              enter-from-class="opacity-0"
              enter-to-class="opacity-100"
              leave-active-class="duration-300 ease-in"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <div
                v-if="subLinks[hoveredSubLinkIndex]?.subLinks?.length > 0"
                ref="buttonGroupRef"
                :key="hoveredSubLinkIndex"
                class="mb-4 flex w-full flex-wrap items-center gap-x-7 gap-y-4 border-t border-avu-black pt-6 xl:gap-x-[60px] xl:gap-y-6"
              >
                <NavButton
                  v-for="link in subLinks[hoveredSubLinkIndex]?.subLinks"
                  :key="link.text"
                  :text="link.text"
                  :link="link.link"
                  class="h-max w-max"
                />
              </div>
            </Transition>
          </div>

          <div
            class="-ml-[30px] grid h-[16.3vw] w-full grid-cols-[19vw_20vw_20vw_20vw_20vw] text-avu-black xl:-ml-[58px]"
          >
            <LetterSvgGroup
              :class="'flex items-start justify-center first:justify-start'"
            />
          </div>
        </div>
      </div>
    </Transition>
  </nav>
</template>

<script setup>
  const route = useRoute();
  const currentPath = computed(() => route.path.split("/")[1]);

  const headerButtons = [
    {
      text: "L<span class='text-avu-blue'>a</span><span class='text-avu-pink'>r</span>ge",
      key: "home",
      link: "/",
    },
    {
      text: "ABOUT",
      key: "about",
      links: [
        { text: "About LARGE", link: "/about/about-large" },
        { text: "Artistic Research", link: "/about/artistic-research" },
        { text: "People", link: "/about/people" },
        { text: "Podcast", link: "/about/podcast" },
        { text: "Contact", link: "/about/contact" },
      ],
    },
    {
      text: "Events",
      key: "events",
      links: [],
    },
    {
      text: "Publications",
      key: "publications",
      links: [],
    },
    { text: "Documents", key: "documents", link: "/documents" },
    { text: "Courses", key: "courses", link: "/courses" },
    { text: "Research", key: "research", link: "/research" },
    { text: "News", key: "news", link: "/news" },
  ];
  const buttonGroupRef = ref(null);
  const isMenuOpen = ref(false);
  const currentOpenMenu = ref(null);
  const subLinks = ref([]);
  const hoveredSubLinkIndex = ref(null);
  const isTransitioning = ref(false);
  let timer = null;

  const { data: queryData } = await usePersistedFetch("header");

  const handleClick = (index, button) => {
    if (isMenuOpen.value && button.links?.length > 0) {
      isMenuOpen.value = false;
      currentOpenMenu.value = null;
      subLinks.value = [];
      document.body.classList.remove("no-scroll");
      return;
    }

    if (!button.links || button.links.length === 0) {
      isMenuOpen.value = false;
      currentOpenMenu.value = null;
      subLinks.value = [];
      document.body.classList.remove("no-scroll");
      return;
    }

    if (!isMenuOpen.value) {
      timer = setTimeout(() => {
        if (index !== 0 && button.links?.length > 0) {
          isMenuOpen.value = true;
          document.body.classList.add("no-scroll");
          updateMenu(button);
        }
      }, 200);
    } else {
      updateMenu(button);
    }
  };

  const handleMouseEnter = (button) => {
    if (isMenuOpen.value) {
      updateMenu(button);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      isMenuOpen.value = false;
      document.body.classList.remove("no-scroll");
      currentOpenMenu.value = null;
    }
  };

  const handleClickOutsideButtons = (event) => {
    if (
      isMenuOpen.value &&
      buttonGroupRef.value &&
      !buttonGroupRef.value.contains(event.target)
    ) {
      isMenuOpen.value = false;
      currentOpenMenu.value = null;
      document.body.classList.remove("no-scroll");
    }
  };

  const updateMenu = (button) => {
    currentOpenMenu.value = button.key;
    subLinks.value = button.links || [];
    hoveredSubLinkIndex.value = null;
  };

  onMounted(() => {
    document.addEventListener("click", handleClickOutsideButtons);
    window.addEventListener("keydown", handleKeyDown);
    document.body.classList.remove("no-scroll");
  });

  onBeforeUnmount(() => {
    document.removeEventListener("click", handleClickOutsideButtons);
    window.removeEventListener("keydown", handleKeyDown);
  });

  watch(route, () => {
    isMenuOpen.value = false;
    document.body.classList.remove("no-scroll");
    currentOpenMenu.value = null;

    isTransitioning.value = true;
    setTimeout(() => {
      isTransitioning.value = false;
    }, 500);
  });

  const handleSubLinkHover = (index) => {
    hoveredSubLinkIndex.value = index;
  };

  watchEffect(() => {
    if (!queryData.value) return;

    const eventButton = headerButtons.find((button) => button.key === "events");
    const publicationButton = headerButtons.find(
      (button) => button.key === "publications"
    );

    if (eventButton) {
      eventButton.links = [];
      queryData.value.eventCategories?.forEach((category) => {
        eventButton.links.push({
          text: category.name,
          link: `/events/${category.slug}`,
          subLinks:
            category.events
              ?.map((event) => ({
                text: event.name,
                link: event.link ?? `/events/${category.slug}/${event.slug}`,
              }))
              .sort((a, b) => b.text.localeCompare(a.text)) || [],
        });
      });

      // TODO: change when there will be no body in the library

      // const performativeConference = eventButton.links.find(
      //   (category) => category.text === "12h Performative Conference"
      // );

      // const hasBodyInTheLibrary = performativeConference?.subLinks?.some(
      //   (link) => link.name === "The Body in the Library"
      // );

      // if (!hasBodyInTheLibrary) {
      //   performativeConference.subLinks.unshift({
      //     text: "The Body in the Library",
      //     link: "/events/12h-performative-conference/the-body-in-the-library",
      //   });
      // }
    }

    if (publicationButton) {
      publicationButton.links = [];
      queryData.value.publications
        ?.filter((publication) => !publication.parent)
        .forEach((publication) => {
          publicationButton.links.push({
            text: publication.name,
            link: `/publication/${publication.slug}`,
            subLinks:
              publication.children?.map((child) => ({
                text: child.name,
                link: `/publication/${publication.slug}/${child.slug}`,
              })) || [],
          });
        });
    }
  });
</script>
